import React, {useState, useEffect, useRef} from "react";
import { useLocation } from "react-router-dom";
import useNav from "./hooks/useNav";
import Header from "./components/Header";
import {
    Loader,
    Shield,
    AlertCircle,
    DollarSign
} from "lucide-react";
import BackButton from "./components/BackButton";
import { usePayment } from "./context/PaymentContext.jsx";
import { useAuthFetch } from "./hooks/useAuthFetch";
import { useCart } from "./context/CartContext.jsx";
import { useUser } from "./context/UserContext.jsx";
import PaymentProcessor from "./components/payment/PaymentProcessor";
import appConfig from "./config";
import { toast } from "react-toastify";
import { PAYMENT_METHODS } from "./config/payments";

const PaymentProcessingPage = () => {
    const [localStatus, setLocalStatus] = useState('processing');
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [isRetrying, setIsRetrying] = useState(false);



    const navigate = useNav();
    const location = useLocation();
    const { clearCart } = useCart();
    const { user } = useUser();
    const {
        status: paymentContextStatus,
        providerInstance,
        provider,
        initPayment,
        restaurantSettings,
        startPayment,
        confirmPayment,
        completePayment,
        failPayment,
        resetPayment,
        isProcessing,
        isSuccess,
        isFailed,
        getProviderInfo
    } = usePayment();
    const { authFetch } = useAuthFetch();

    const { order, paymentMethod, restaurantId } = location.state || {};
    // Создаем refs для providerInstance и localStatus
    const providerInstanceRef = useRef(providerInstance);
    const localStatusRef = useRef(localStatus);

    console.log('PaymentProcessingPage рендерится', {
        order: order,
        paymentMethod: paymentMethod,
        restaurantId: restaurantId,
        orderId: order?.id,
        orderAmount: order?.amount
    });

    // Синхронизируем локальный статус с контекстом
    useEffect(() => {
        if (isSuccess) {
            setLocalStatus('success');
        } else if (isFailed) {
            setLocalStatus('failed');
            setErrorMessage(errorMessage || "Платеж не удался");
        }
    }, [isSuccess, isFailed]);

    useEffect(() => {
        if (providerInstance && localStatus === 'processing') {
            setLocalStatus('ready');
        }
    }, [providerInstance, localStatus]);

    // Инициализация платежа
    useEffect(() => {
        if (!order || !restaurantId) {
            toast.error("Данные платежа не найдены");
            navigate("/");
            return;
        }

        // Проверяем, поддерживается ли метод оплаты
        if (paymentMethod && ![PAYMENT_METHODS.CARD, PAYMENT_METHODS.SBP, PAYMENT_METHODS.CASH].includes(paymentMethod)) {
            toast.error("Неподдерживаемый метод оплаты");
            navigate("/payment");
            return;
        }

        initializePayment();
    }, []);

    // Синхронизируем ref с состоянием при каждом обновлении
    useEffect(() => {
        providerInstanceRef.current = providerInstance;
    }, [providerInstance]);

    useEffect(() => {
        localStatusRef.current = localStatus;
    }, [localStatus]);

    // Таймаут для диагностики
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!providerInstanceRef.current && localStatusRef.current === 'processing') {
                console.error('Таймаут инициализации платежа');
                setLocalStatus('failed');
                setErrorMessage('Превышено время ожидания платежной системы');
            }
        }, 10000);

        return () => clearTimeout(timeout);
    }, []);

    const initializePayment = async () => {
        console.log('initializePayment start', {
            orderId: order?.id,
            amount: order?.amount,
            method: paymentMethod,
            userId: user?.id
        });
        try {
            const isDev = process.env.NODE_ENV === 'development';

            const startOptions = { forceMock: isDev };
            if (isDev) {
                startOptions.testScenario = localStorage.getItem('paymentScenario') || 'SUCCESS';
            }

            const instance = await startPayment({
                orderId: order.id,
                id: order.id,
                amount: order.amount,
                method: paymentMethod,
                description: `Заказ #${order.id}`,
                userId: user?.id,
                items: order.items,
                restaurantId: restaurantId,
            }, restaurantId, order.amount, startOptions);

            console.log('startPayment успешно завершен');

        } catch (error) {
            console.error("Payment initialization failed:", error);
            handlePaymentError(error);
        }
    };

    const handlePaymentSuccess = async (paymentResult) => {
        try {
            // Подтверждаем платеж через провайдера
            const confirmResult = await confirmPayment(paymentResult);

            // Отправляем подтверждение на бэкенд
            const response = await authFetch(`${appConfig.endpoint}/orders/${order.id}/confirm-payment`, {
                method: "POST",
                body: JSON.stringify({
                    transactionId: paymentResult.transactionId,
                    paymentMethod: paymentMethod,
                    provider: provider,
                    paymentData: paymentResult,
                    confirmData: confirmResult,
                    status: "paid"
                })
            });

            if (response.success) {
                completePayment(paymentResult);
                setPaymentDetails(paymentResult);
                clearCart();

                setTimeout(() => {
                    navigate("/order-success", {
                        state: {
                            order: response.order || order,
                            paymentMethod: paymentMethod,
                            paymentDetails: paymentResult,
                            provider: provider
                        }
                    });
                }, 2000);
            } else {
                throw new Error(response.error || "Ошибка подтверждения платежа");
            }
        } catch (error) {
            console.error("Payment confirmation error:", error);

            // Если платеж прошел, но подтверждение не удалось
            if (paymentResult.success && error.message.includes('already')) {
                setLocalStatus('success');
                setPaymentDetails(paymentResult);
                clearCart();

                setTimeout(() => {
                    navigate("/order-success", {
                        state: {
                            order: order,
                            paymentMethod: paymentMethod,
                            paymentDetails: paymentResult,
                            provider: provider,
                            warning: "Платеж прошел, но требуется подтверждение администратора"
                        }
                    });
                }, 3000);
            } else {
                handlePaymentError(error);
                throw error; // propagate to PaymentProcessor so it doesn't show success
            }
        }
    };

    const handlePaymentError = (error) => {
        console.error("Payment error:", error);
        setLocalStatus('failed');
        setErrorMessage(error.message || "Произошла ошибка при оплате");
        failPayment(error);
        toast.error("Ошибка оплаты");
    };

    const handleRetry = () => {
        setIsRetrying(true);
        setLocalStatus('processing');
        setErrorMessage("");
        resetPayment();

        // Даем время контексту сброситься
        setTimeout(() => {
            initializePayment();
            setIsRetrying(false);
        }, 100);
    };

    const handleCancel = () => {
        resetPayment();
        navigate("/payment", { state: { order } });
    };

    const handleBack = () => {
        navigate(-1);
    };

    // Для наличных - свой упрощенный UI
    if (paymentMethod === PAYMENT_METHODS.CASH) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header />
                <div className="relative max-w-screen-md mx-auto px-4 pt-6">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex flex-col items-center justify-center py-8">
                            <DollarSign className="text-green-500 mb-4" size={64} />
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Заказ подтвержден!
                            </h2>
                            <p className="text-gray-600 text-center mb-6">
                                Оплата при получении наличными
                            </p>
                            <button
                                onClick={() => navigate("/order-success", {
                                    state: { order, paymentMethod: "cash" }
                                })}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
                            >
                                Перейти к заказу
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const providerInfo = getProviderInfo();

    // Показываем загрузку при инициализации или retry
    const isLoading = !providerInstance && localStatus === 'processing';
    console.log('providerInstance: ', !providerInstance, localStatus === 'processing', 'isLoading:', isLoading);
    if (isLoading || isRetrying) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header />
                <div className="relative max-w-screen-md mx-auto px-4 pt-6">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader className="animate-spin text-blue-500 mb-4" size={48} />
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                {isRetrying ? "Повторная попытка..." : "Подготовка платежной системы..."}
                            </h2>
                            <p className="text-gray-600">
                                {providerInfo?.name || 'Загрузка...'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header />

            <div className="relative max-w-screen-md mx-auto px-4 pt-6">
                {/* Кнопка назад */}
                {localStatus === "failed" && (
                    <div className="mb-6">
                        <BackButton onClick={handleBack} />
                    </div>
                )}

                {/* Основной контент */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    {/* Информация о тестовом режиме */}
                    {process.env.NODE_ENV === 'development' && (
                        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="text-purple-500" size={18} />
                                    <span className="text-sm text-purple-800">
                                        Режим разработки
                                    </span>
                                </div>
                                <select
                                    onChange={(e) => {
                                        localStorage.setItem('paymentScenario', e.target.value);
                                        window.location.reload();
                                    }}
                                    className="text-xs border border-purple-200 rounded px-2 py-1"
                                    defaultValue={localStorage.getItem('paymentScenario') || 'SUCCESS'}
                                >
                                    <option value="SUCCESS">Успех</option>
                                    <option value="FAILED">Ошибка</option>
                                    <option value="THREEDS">3D-Secure</option>
                                </select>
                            </div>
                        </div>
                    )}

                    <PaymentProcessor
                        order={{ ...order, paymentMethod }}
                        user={user}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        onCancel={handleCancel}
                        onRetry={handleRetry}
                        currentStatus={localStatus}
                        errorMessage={errorMessage}
                    />
                </div>

                {/* Детали заказа */}
                <div className="bg-white rounded-lg shadow-sm p-6 mt-4">
                    <h3 className="font-medium text-gray-900 mb-3">Детали заказа</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Номер заказа:</span>
                            <span className="font-medium">#{order?.id}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Сумма:</span>
                            <span className="font-medium">{order?.amount} ₽</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Способ оплаты:</span>
                            <span className="font-medium">
                                {paymentMethod === PAYMENT_METHODS.CARD && "Карта онлайн"}
                                {paymentMethod === PAYMENT_METHODS.SBP && "СБП"}
                            </span>
                        </div>
                        {providerInfo && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Платежная система:</span>
                                <span className="font-medium">
                                    {providerInfo.name}
                                    {providerInfo.testMode && " (тест)"}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Информация о безопасности */}
                <div className="mt-4 text-center">
                    <div className="inline-flex items-center text-xs text-gray-500">
                        <Shield className="mr-1 text-green-500" size={14} />
                        <span>Безопасное соединение</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentProcessingPage;